import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { LogOut, Ticket, User } from 'lucide-react';
import { ROLE_LABELS, type CurrentUser } from '@electrodavisan/shared';
import { ROUTES } from '../../app/config';
import { initialsOf } from '../../auth/useSession';
import { cn } from '../../lib/cn';

export function UserMenu({
  user,
  compact = false,
  onNavigate,
}: {
  user: CurrentUser | undefined;
  compact?: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /**
   * Cierre de sesion correcto:
   *  1. Se descarta la sesion local (tokens en memoria/sessionStorage).
   *  2. Se redirige al `end_session_endpoint` de Keycloak, que termina
   *     tambien la sesion SSO del realm.
   *  3. Keycloak devuelve al `post_logout_redirect_uri` del Ecosystem.
   * Esto es lo que deja preparado el Single Logout para las apps futuras.
   */
  const handleLogout = (): void => {
    setOpen(false);
    void auth.signoutRedirect({ post_logout_redirect_uri: `${window.location.origin}/` }).catch(
      () => {
        // Si Keycloak no responde, al menos se limpia la sesion local.
        void auth.removeUser();
        navigate(ROUTES.login, { replace: true });
      },
    );
  };

  const go = (path: string): void => {
    setOpen(false);
    onNavigate?.();
    navigate(path);
  };

  const primaryRole = user?.roles[0];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors',
          'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          compact && 'justify-center',
        )}
      >
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary-bright"
        >
          {initialsOf(user)}
        </span>
        {!compact ? (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-text-primary">
              {user?.fullName ?? 'Cargando...'}
            </span>
            <span className="block truncate text-[11px] text-text-muted">
              {primaryRole ? ROLE_LABELS[primaryRole] : (user?.email ?? '')}
            </span>
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-30 mb-2 w-60 animate-fade-in overflow-hidden rounded-card border border-border bg-surface shadow-overlay"
        >
          <div className="border-b border-border px-3 py-3">
            <p className="truncate text-sm font-medium text-text-primary">{user?.fullName}</p>
            <p className="truncate text-[13px] text-text-muted">{user?.email}</p>
            {user && user.roles.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary-bright"
                  >
                    {ROLE_LABELS[role]}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="p-1">
            <MenuItem icon={<User className="h-4 w-4" />} onClick={() => go(ROUTES.profile)}>
              Mi perfil
            </MenuItem>
            <MenuItem icon={<Ticket className="h-4 w-4" />} onClick={() => go(ROUTES.tickets)}>
              Mis tickets
            </MenuItem>
            <MenuItem icon={<LogOut className="h-4 w-4" />} onClick={handleLogout} danger>
              Cerrar sesion
            </MenuItem>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  icon,
  children,
  onClick,
  danger,
}: {
  icon: JSX.Element;
  children: string;
  onClick: () => void;
  danger?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
        danger
          ? 'text-danger hover:bg-danger-soft'
          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
      )}
    >
      <span aria-hidden className="shrink-0">
        {icon}
      </span>
      {children}
    </button>
  );
}
